// =====================================================
// COMPOSANT: ConversionSection — « Conversion & traçabilité commerciale » (RUN 3)
// =====================================================
// Deux cartes (wireframe « two ») :
//   Gauche — Taux de conversion BPI → ID (grand chiffre + Δ N-1 pts) + entonnoir
//            dédoublé (BPI→PI, PI→ID) + tableau commerciaux dépliable.
//   Droite — Taux édition N : annulation + report (+ définitions).
// Design system crm-4l (zinc), Δ en points via RateDelta. Aucune donnée mockée.

import { AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { ConversionData, RateValue } from '@/lib/dashboard-stats';
import { CommercialsTable } from './CommercialsTable';
import { RateDelta } from './RateDelta';
import { fmtRate } from './rate-format';

/** Sous-carte d'un taux d'étape (BPI→PI, PI→ID, annulation, report). */
function RateCard({
  rate,
  label,
  goodDirection,
  showN1,
  warnOverflow = false,
}: {
  rate: RateValue;
  label: string;
  goodDirection: 'up' | 'down';
  showN1: boolean;
  /** Affiche un avertissement si le taux dépasse 100 % (num > den). */
  warnOverflow?: boolean;
}) {
  const overflow = warnOverflow && rate.value != null && rate.value > 1;
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-zinc-200 bg-white px-4 py-3">
      <span className="text-3xl font-bold tabular-nums text-zinc-900">{fmtRate(rate.value)}</span>
      <span className="text-xs font-semibold text-zinc-600">{label}</span>
      {showN1 ? <RateDelta rate={rate} goodDirection={goodDirection} className="mt-1" /> : null}
      {overflow ? (
        <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-600">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          PI (équipages) &gt; BPI payés enregistrés
        </span>
      ) : null}
    </div>
  );
}

interface ConversionSectionProps {
  conversion: ConversionData;
  showN1: boolean;
}

export function ConversionSection({ conversion, showN1 }: ConversionSectionProps) {
  const { funnel, commercials, editionRates } = conversion;

  return (
    <section className="flex flex-col gap-6">
      {/* Titre de section */}
      <div className="flex items-baseline gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-700">
          Conversion &amp; traçabilité commerciale
        </h2>
        <div className="h-px flex-1 bg-zinc-200" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.7fr_1fr]">
        {/* ── Gauche : conversion + tableau commerciaux ── */}
        <Card className="border border-zinc-200">
          <CardContent className="flex flex-col gap-4 px-6">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h3 className="text-sm font-semibold text-zinc-950">Taux de conversion BPI → ID</h3>
              <span className="rounded-md bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                cliquer un commercial → ses stats
              </span>
            </div>

            {/* Grand chiffre BPI → ID */}
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="text-[42px] font-extrabold leading-none tracking-tight tabular-nums text-zinc-900">
                {fmtRate(funnel.bpiToId.value)}
              </span>
              {showN1 ? <RateDelta rate={funnel.bpiToId} goodDirection="up" /> : null}
            </div>

            {/* Entonnoir dédoublé */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <RateCard
                rate={funnel.bpiToPi}
                label="Conversion BPI → PI"
                goodDirection="up"
                showN1={showN1}
                warnOverflow
              />
              <RateCard
                rate={funnel.piToId}
                label="Conversion PI → ID"
                goodDirection="up"
                showN1={showN1}
              />
            </div>

            {/* Tableau commerciaux dépliable */}
            <CommercialsTable rows={commercials} />
          </CardContent>
        </Card>

        {/* ── Droite : taux édition N ── */}
        <Card className="border border-zinc-200">
          <CardContent className="flex flex-col gap-4 px-6">
            <h3 className="text-sm font-semibold text-zinc-950">Taux édition N</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <RateCard
                rate={editionRates.annulation}
                label="Taux d'annulation"
                goodDirection="down"
                showN1={showN1}
              />
              <RateCard
                rate={editionRates.report}
                label="Taux de report"
                goodDirection="down"
                showN1={showN1}
              />
            </div>
            <p className="mt-2 text-xs leading-relaxed text-zinc-500">
              <span className="font-medium text-zinc-600">Annulation</span> = inscriptions
              définitives résiliées sur l'édition.
              <br />
              <span className="font-medium text-zinc-600">Report</span> = équipages basculés sur
              l'édition suivante.
              <br />
              <span className="text-zinc-400">
                Base = cohorte ayant atteint l'inscription définitive (actifs + résiliés +
                reportés).
              </span>
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
